// =============================================================================
// MessageComposer/useWindowState.ts — Verifica se a janela 24h está aberta.
//
// Usa o ComposerWindowState retornado por useConversation(id).
// A janela está "aberta" quando:
//   - composerState.window === 'open' OU 'human_agent_tag'
//
// staleTime: 60s para não sobrecarregar a API com polls frequentes.
// =============================================================================

import { READ_ONLY_NOTICE } from '../../assignment';
import { useConversation } from '../../queries';
import type { ComposerWindowKind } from '../../types';

export interface WindowStateResult {
  /**
   * F43-S27: a conversa é SOMENTE LEITURA para este usuário (já tem outro
   * atendente E é de cidade fora do escopo dele). O bloqueio de verdade está
   * no backend, no caminho de envio — isto aqui é só para a tela explicar em
   * vez de deixar a pessoa escrever e tomar 403.
   */
  readOnly: boolean;
  /** Frase pronta do motivo; `null` quando a conversa é editável. */
  readOnlyNotice: string | null;
  /** true se o atendente pode enviar texto livre */
  windowOpen: boolean;
  /** Estado exato da janela (para exibir mensagem contextual) */
  windowKind: ComposerWindowKind | null;
  /** Milissegundos restantes (null = sem janela ou provider WAHA) */
  remainingMs: number | null;
  /** Query ainda carregando */
  isLoading: boolean;
}

/**
 * useWindowState — verifica se a janela de 24h da conversa está aberta.
 *
 * Depende de useConversation que retorna composerState.
 * staleTime herdado do QueryClient global (30s),
 * re-fetch no refocus (evita exibir janela expirada para atendente ativo).
 *
 * A decisão de abrir/fechar é feita no BACKEND. O frontend apenas exibe.
 */
export function useWindowState(conversationId: string): WindowStateResult {
  const { data, isLoading } = useConversation(conversationId);

  if (!data) {
    return {
      windowOpen: false,
      windowKind: null,
      remainingMs: null,
      isLoading,
      readOnly: false,
      readOnlyNotice: null,
    };
  }

  const { composerState } = data;
  const windowOpen = composerState.window === 'open' || composerState.window === 'human_agent_tag';

  const readOnlyReason = data.access?.readOnlyReason ?? null;

  return {
    windowOpen,
    windowKind: composerState.window,
    remainingMs: composerState.remainingMs,
    isLoading,
    readOnly: readOnlyReason !== null,
    readOnlyNotice: readOnlyReason !== null ? READ_ONLY_NOTICE[readOnlyReason] : null,
  };
}
